import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, Loader2, Truck, FileText, Plus, CheckCircle2, Clock, AlertCircle,
  Package, FileCheck, Download, Eye, Copy, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  draft:      { label: 'Bozza',          color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', next: ['confirmed', 'cancelled'] },
  confirmed:  { label: 'Confermato',     color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', next: ['processing', 'cancelled'] },
  processing: { label: 'In Lavorazione', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', next: ['shipped'] },
  shipped:    { label: 'Spedito',        color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', next: ['delivered'] },
  delivered:  { label: 'Consegnato',     color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', next: [] },
  cancelled:  { label: 'Annullato',      color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', next: [] },
};

const DEL_STATUS = {
  pending:    { label: 'In attesa',    color: 'text-slate-500',  icon: Clock },
  scheduled:  { label: 'Programmata', color: 'text-blue-500',   icon: Package },
  in_transit: { label: 'In transito', color: 'text-purple-500', icon: Truck },
  delivered:  { label: 'Consegnata',  color: 'text-green-500',  icon: CheckCircle2 },
  failed:     { label: 'Fallita',     color: 'text-red-500',    icon: AlertCircle },
};

const formatEuro = (v) =>
  `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [companyCategory, setCompanyCategory] = useState('');
  const [ddtType, setDdtType] = useState('');  // chosen DDT type (overrides company default)
  const [loading, setLoading] = useState(true);
  const [showDelivery, setShowDelivery] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ddtLoading, setDdtLoading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [delForm, setDelForm] = useState({ scheduled_date: '', carrier: '', tracking_number: '', notes: '' });
  const [invNotes, setInvNotes] = useState('');
  const [payMethod, setPayMethod] = useState('bonifico');
  const [dueDays, setDueDays] = useState(30);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${id}`);
      setOrder(res.data);
      // Also fetch company to get customer_category default
      if (res.data.company_id) {
        try {
          const c = await axios.get(`${API}/companies/${res.data.company_id}`);
          const cat = c.data.customer_category || '';
          setCompanyCategory(cat);
          setDdtType(prev => prev || cat || 'gdo');
        } catch { /* non-blocking */ }
      }
    } catch { toast.error('Ordine non trovato'); navigate('/orders'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchOrder(); }, [id]);

  const updateStatus = async (status) => {
    try {
      await axios.patch(`${API}/orders/${id}/status`, { status });
      toast.success(`Stato aggiornato: ${STATUS_CONFIG[status]?.label}`);
      fetchOrder();
    } catch { toast.error('Errore'); }
  };

  const createDelivery = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/orders/${id}/deliveries`, { order_id: id, ...delForm });
      toast.success('Consegna creata');
      setShowDelivery(false);
      setDelForm({ scheduled_date: '', carrier: '', tracking_number: '', notes: '' });
      fetchOrder();
    } catch { toast.error('Errore'); } finally { setSaving(false); }
  };

  const updateDelivery = async (delId, status) => {
    try {
      await axios.patch(`${API}/orders/deliveries/${delId}`, { status });
      toast.success('Stato consegna aggiornato');
      fetchOrder();
    } catch { toast.error('Errore'); }
  };

  const createInvoice = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/invoices`, {
        order_id: id,
        company_id: order.company_id,
        contact_id: order.contact_id,
        items: order.items.map(it => ({
          description: it.description,
          quantity: it.quantity,
          unit_price: it.unit_price,
          discount: it.discount || 0,
          vat_rate: it.vat_rate || 22,
        })),
        due_days: dueDays,
        payment_method: payMethod,
        notes: invNotes,
      });
      toast.success(`Fattura ${res.data.invoice_number} creata`);
      setShowInvoice(false);
      fetchOrder();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  // ── Duplicate order ──────────────────────────────────────────────────────────
  const handleDuplicate = async () => {
    if (!window.confirm('Vuoi duplicare questo ordine? Verrà creata una nuova bozza con gli stessi articoli e dati.')) return;
    setDuplicating(true);
    try {
      const res = await axios.post(`${API}/orders/${id}/duplicate`);
      toast.success(`Ordine duplicato: ${res.data.order_number}`);
      navigate(`/orders/${res.data.id}`);
    } catch { toast.error('Errore nella duplicazione'); }
    finally { setDuplicating(false); }
  };

  // ── DDT generation ──────────────────────────────────────────────────────────
  const handleDDT = async (forDownload = false, ddtType = null) => {
    setDdtLoading(true);
    try {
      const params = [];
      if (forDownload) params.push('download=true');
      if (ddtType) params.push(`type=${ddtType}`);
      const url = `${API}/ddt/generate/${id}${params.length ? '?' + params.join('&') : ''}`;
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(blob);

      if (forDownload) {
        const ddtNum = order.ddt_number || order.order_number;
        const suffix = ddtType === 'distributore' ? '_DIST' : '';
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `DDT_${ddtNum.replace(/-/g, '_')}${suffix}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
        toast.success(`DDT ${ddtType === 'distributore' ? 'Distributore' : 'GDO'} scaricato`);
      } else {
        window.open(objectUrl, '_blank');
        // Refresh order to get the newly assigned DDT number
        fetchOrder();
      }
    } catch (e) {
      toast.error('Errore nella generazione del DDT');
    } finally {
      setDdtLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!order) return null;

  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.draft;
  const nextStatuses = cfg.next || [];
  const hasInvoice = !!order.invoice;

  // VAT breakdown by rate
  const vatBreakdown = {};
  order.items?.forEach(it => {
    const rate = it.vat_rate || 22;
    const net  = it.line_net ?? (it.quantity * it.unit_price * (1 - (it.discount || 0) / 100));
    const vat  = it.line_vat ?? net * rate / 100;
    if (!vatBreakdown[rate]) vatBreakdown[rate] = { net: 0, vat: 0 };
    vatBreakdown[rate].net += net;
    vatBreakdown[rate].vat += vat;
  });

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/orders')}><ArrowLeft size={18} /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold font-mono" style={{ fontFamily: 'Manrope, sans-serif' }}>{order.order_number}</h1>
            <span className={`text-sm px-2.5 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
            {order.ddt_number && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 font-mono font-medium">
                DDT {order.ddt_number}
              </span>
            )}
          </div>
          <p className="text-muted-foreground text-sm mt-0.5">
            {order.company_name} {order.contact_name ? `• ${order.contact_name}` : ''}
          </p>
        </div>
        {/* Status actions + Duplicate */}
        <div className="flex gap-2 flex-wrap">
          {order.status === 'draft' && (
            <Button
              variant="outline" size="sm"
              onClick={() => navigate(`/orders?edit=${id}`)}
              data-testid="edit-order-btn"
            >
              <Pencil size={13} className="mr-1.5" />
              Modifica
            </Button>
          )}
          <Button
            variant="outline" size="sm"
            onClick={handleDuplicate}
            disabled={duplicating}
            data-testid="duplicate-order-btn"
          >
            {duplicating ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Copy size={13} className="mr-1.5" />}
            Duplica
          </Button>
          {nextStatuses.map(s => (
            <Button key={s} variant="outline" size="sm" onClick={() => updateStatus(s)} data-testid={`status-${s}`}>
              <span className={`w-2 h-2 rounded-full mr-1.5 inline-block ${STATUS_CONFIG[s]?.color.split(' ')[0]}`} />
              {STATUS_CONFIG[s]?.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Draft warning banner with prominent Conferma CTA */}
      {order.status === 'draft' && (
        <div className="rounded-lg border-2 border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="draft-banner">
          <AlertCircle size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">Questo ordine è in <span className="uppercase">Bozza</span></p>
            <p className="text-xs text-amber-700 dark:text-amber-300/80 mt-0.5">Puoi ancora modificarlo. Una volta confermato non sarà più editabile.</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => navigate(`/orders?edit=${id}`)}
              data-testid="banner-edit-btn"
              className="border-amber-400 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/40"
            >
              <Pencil size={13} className="mr-1.5" />
              Modifica
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (window.confirm('Confermi questo ordine? Dopo la conferma non potrà più essere modificato.')) {
                  updateStatus('confirmed');
                }
              }}
              data-testid="banner-confirm-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <CheckCircle2 size={13} className="mr-1.5" />
              Conferma Ordine
            </Button>
          </div>
        </div>
      )}

      {/* Locked banner for non-draft, non-final states */}
      {order.status !== 'draft' && !['delivered', 'cancelled'].includes(order.status) && (
        <div className="rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/15 px-4 py-2.5 flex items-center gap-3" data-testid="locked-banner">
          <CheckCircle2 size={18} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <p className="text-sm text-blue-900 dark:text-blue-100">
            Ordine <span className="font-semibold">{cfg.label.toLowerCase()}</span> — non più modificabile per garantire l'integrità dei documenti emessi.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: info */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase font-semibold text-muted-foreground tracking-wider">Dettagli Ordine</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Data ordine</span><span>{order.order_date ? new Date(order.order_date).toLocaleDateString('it-IT') : '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Consegna prevista</span><span>{order.expected_delivery ? new Date(order.expected_delivery).toLocaleDateString('it-IT') : '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Commerciale</span><span>{order.assigned_name || '-'}</span></div>
              {order.riferimento && <div className="flex justify-between"><span className="text-muted-foreground">Riferimento</span><span className="font-mono text-xs">{order.riferimento}</span></div>}
              {order.cod_cliente && <div className="flex justify-between"><span className="text-muted-foreground">Cod. Cliente</span><span className="font-mono text-xs">{order.cod_cliente}</span></div>}
              {order.notes && <div className="pt-1"><p className="text-xs text-muted-foreground mb-0.5">Note</p><p>{order.notes}</p></div>}
            </CardContent>
          </Card>

          {/* Riepilogo importi */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase font-semibold text-muted-foreground tracking-wider">Riepilogo Importi</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Imponibile</span><span>{formatEuro(order.subtotal)}</span></div>
              {Object.entries(vatBreakdown).map(([rate, data]) => (
                <div key={rate} className="flex justify-between text-xs">
                  <span className="text-muted-foreground">IVA {rate}% su {formatEuro(data.net)}</span>
                  <span>{formatEuro(data.vat)}</span>
                </div>
              ))}
              <Separator />
              <div className="flex justify-between font-bold text-base">
                <span>Totale</span>
                <span className="text-primary">{formatEuro(order.total)}</span>
              </div>
            </CardContent>
          </Card>

          {/* DDT Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase font-semibold text-muted-foreground tracking-wider">
                Documento di Trasporto (DDT)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {order.ddt_number ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Numero DDT</span>
                    <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">{order.ddt_number}</span>
                  </div>
                  {order.ddt_generated_at && (
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Generato il</span>
                      <span>{new Date(order.ddt_generated_at).toLocaleDateString('it-IT')}</span>
                    </div>
                  )}
                  <Separator />
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Nessun DDT generato.</p>
              )}
              {/* DDT Type selector */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tipo DDT</Label>
                <select
                  value={ddtType}
                  onChange={e => setDdtType(e.target.value)}
                  className="h-8 text-xs w-full border border-input rounded-md px-2 bg-background"
                  data-testid="ddt-type-select"
                >
                  <option value="gdo">GDO (senza prezzi){companyCategory === 'gdo' ? ' — default cliente' : ''}</option>
                  <option value="distributore">Distributore (con prezzi){companyCategory === 'distributore' ? ' — default cliente' : ''}</option>
                </select>
                {ddtType !== companyCategory && companyCategory && (
                  <p className="text-[10px] text-amber-600 dark:text-amber-400">⚠ Override del default cliente ({companyCategory === 'distributore' ? 'Distributore' : 'GDO'})</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline" className="flex-1"
                  onClick={() => handleDDT(false, ddtType)}
                  disabled={ddtLoading}
                  data-testid="preview-ddt-btn"
                >
                  {ddtLoading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Eye size={13} className="mr-1.5" />}
                  Anteprima
                </Button>
                <Button
                  size="sm" className="flex-1"
                  onClick={() => handleDDT(true, ddtType)}
                  disabled={ddtLoading}
                  data-testid="download-ddt-btn"
                >
                  {ddtLoading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Download size={13} className="mr-1.5" />}
                  Scarica PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: items + deliveries + invoice */}
        <div className="lg:col-span-2 space-y-6">
          {/* Items table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase font-semibold text-muted-foreground tracking-wider">Articoli</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ minWidth: '650px' }}>
                  <thead>
                    <tr className="bg-muted/30 border-b border-border">
                      <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Codice</th>
                      <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Descrizione / Lotto</th>
                      <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">U.M.</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Qtà</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Colli</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground hidden md:table-cell">Prezzo</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground hidden md:table-cell">Sc%</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">IVA</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items?.map((it, i) => {
                      const net = it.line_net ?? (it.quantity * it.unit_price * (1 - (it.discount || 0) / 100));
                      const vat = it.line_vat ?? net * (it.vat_rate || 22) / 100;
                      return (
                        <tr key={`${it.product_code || ''}-${it.lot_number || ''}-${i}`} className="border-b border-border last:border-0">
                          <td className="px-3 py-2.5">
                            <span className="font-mono text-xs text-muted-foreground">{it.product_code || '-'}</span>
                          </td>
                          <td className="px-3 py-2.5">
                            <p className="font-medium">{it.description}</p>
                            {it.lot_number && <p className="text-xs text-muted-foreground">Lotto: {it.lot_number}</p>}
                          </td>
                          <td className="px-3 py-2.5 text-center text-xs text-muted-foreground">{it.unit_measure || 'NR'}</td>
                          <td className="px-3 py-2.5 text-right">{it.quantity}</td>
                          <td className="px-3 py-2.5 text-right text-muted-foreground">{it.num_colli ?? it.colli ?? '-'}</td>
                          <td className="px-3 py-2.5 text-right hidden md:table-cell">{formatEuro(it.unit_price)}</td>
                          <td className="px-3 py-2.5 text-right hidden md:table-cell text-muted-foreground">{it.discount || 0}%</td>
                          <td className="px-3 py-2.5 text-right text-muted-foreground">{it.vat_rate || 22}%</td>
                          <td className="px-3 py-2.5 text-right font-medium">{formatEuro(net + vat)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Packaging summary (only when at least one item has packaging) */}
          {(() => {
            const groups = {};
            (order.items || []).forEach(it => {
              if (!it.packaging_name) return;
              const key = it.packaging_id || it.packaging_name;
              const nc = parseFloat(it.num_colli || it.colli || 0) || 0;
              const L = parseFloat(it.packaging_length_cm || 0);
              const W = parseFloat(it.packaging_width_cm || 0);
              const H = parseFloat(it.packaging_height_cm || 0);
              const unitVol = (L && W && H) ? (L * W * H / 1_000_000) : 0;
              if (groups[key]) {
                groups[key].colli += nc;
                groups[key].volume_tot += nc * unitVol;
              } else {
                groups[key] = {
                  name: it.packaging_name,
                  material: it.packaging_material || '',
                  dims: (L || W || H) ? `${L || '-'}×${W || '-'}×${H || '-'} cm` : '-',
                  unitVol,
                  colli: nc,
                  volume_tot: nc * unitVol,
                };
              }
            });
            const list = Object.values(groups);
            if (!list.length) return null;
            const totColli = list.reduce((s, g) => s + g.colli, 0);
            const totVol = list.reduce((s, g) => s + g.volume_tot, 0);
            const fmt = (v, d = 4) => Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: d });
            return (
              <Card data-testid="packaging-summary-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs uppercase font-semibold text-muted-foreground tracking-wider flex items-center gap-2">
                    <Package size={13} /> Riepilogo Imballaggi
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" style={{ minWidth: '600px' }}>
                      <thead>
                        <tr className="bg-muted/30 border-b border-border">
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Tipo Imballaggio</th>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Materiale</th>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Dimensioni</th>
                          <th className="text-right px-3 py-2 font-medium text-muted-foreground text-xs">N° Colli</th>
                          <th className="text-right px-3 py-2 font-medium text-muted-foreground text-xs">Vol. Unit. (m³)</th>
                          <th className="text-right px-3 py-2 font-medium text-muted-foreground text-xs">Vol. Tot. (m³)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((g) => (
                          <tr key={g.name + g.material + g.dims} className="border-b border-border last:border-0">
                            <td className="px-3 py-2 font-medium">{g.name}</td>
                            <td className="px-3 py-2 text-muted-foreground capitalize">{g.material || '-'}</td>
                            <td className="px-3 py-2 text-muted-foreground text-xs">{g.dims}</td>
                            <td className="px-3 py-2 text-right">{fmt(g.colli, 0)}</td>
                            <td className="px-3 py-2 text-right text-muted-foreground">{g.unitVol ? fmt(g.unitVol) : '-'}</td>
                            <td className="px-3 py-2 text-right font-medium">{g.volume_tot ? fmt(g.volume_tot) : '-'}</td>
                          </tr>
                        ))}
                        <tr className="bg-muted/30 font-semibold">
                          <td className="px-3 py-2" colSpan={3}>TOTALE</td>
                          <td className="px-3 py-2 text-right">{fmt(totColli, 0)}</td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2 text-right text-primary">{fmt(totVol)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* DDT Transport info (if set) */}
          {(order.destinazione_nome || order.causale_trasporto) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase font-semibold text-muted-foreground tracking-wider">Dati Trasporto</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-2 text-sm">
                {order.destinazione_nome && <div><span className="text-xs text-muted-foreground block">Destinazione</span>{order.destinazione_nome}</div>}
                {order.destinazione_indirizzo && <div><span className="text-xs text-muted-foreground block">Indirizzo</span>{order.destinazione_indirizzo}, {order.destinazione_cap_citta}</div>}
                {order.causale_trasporto && <div><span className="text-xs text-muted-foreground block">Causale</span>{order.causale_trasporto}</div>}
                {order.aspetto_beni && <div><span className="text-xs text-muted-foreground block">Aspetto beni</span>{order.aspetto_beni}</div>}
                {order.vettore && <div><span className="text-xs text-muted-foreground block">Vettore</span>{order.vettore}</div>}
                {order.tipo_movimentazione && order.tipo_movimentazione !== 'none' && (
                  <div><span className="text-xs text-muted-foreground block">Tipo Movimentazione</span>
                    {{'S':'S = Sostituzione','A':'A = Assaggio/Campionatura','E':'E = Esposizione'}[order.tipo_movimentazione] || order.tipo_movimentazione}
                  </div>
                )}
                {order.num_colli_totale > 0 && <div><span className="text-xs text-muted-foreground block">Num. Colli</span>{order.num_colli_totale}</div>}
                {order.peso_lordo && <div><span className="text-xs text-muted-foreground block">Peso Lordo</span>{order.peso_lordo}</div>}
                {order.volume && <div><span className="text-xs text-muted-foreground block">Volume</span>{order.volume}</div>}
              </CardContent>
            </Card>
          )}

          {/* Deliveries */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs uppercase font-semibold text-muted-foreground tracking-wider">Consegne ({order.deliveries?.length || 0})</CardTitle>
                <Button size="sm" variant="outline" onClick={() => setShowDelivery(true)} data-testid="add-delivery-btn">
                  <Plus size={13} className="mr-1.5" />Nuova Consegna
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {order.deliveries?.length > 0 ? (
                <div className="space-y-3">
                  {order.deliveries.map(del => {
                    const dCfg = DEL_STATUS[del.status] || DEL_STATUS.pending;
                    const DIcon = dCfg.icon;
                    return (
                      <div key={del._id || del.id} className="flex items-start gap-3 p-3 rounded-lg border border-border">
                        <DIcon size={18} className={`flex-shrink-0 mt-0.5 ${dCfg.color}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono text-sm font-semibold">{del.delivery_number}</span>
                            <span className={`text-xs font-medium ${dCfg.color}`}>{dCfg.label}</span>
                          </div>
                          {del.scheduled_date && <p className="text-xs text-muted-foreground mt-0.5">Data: {new Date(del.scheduled_date).toLocaleDateString('it-IT')}</p>}
                          {del.carrier && <p className="text-xs text-muted-foreground">Corriere: {del.carrier}{del.tracking_number ? ` • ${del.tracking_number}` : ''}</p>}
                          {del.delivered_at && <p className="text-xs text-green-600 dark:text-green-400">Consegnata: {new Date(del.delivered_at).toLocaleDateString('it-IT')}</p>}
                        </div>
                        {del.status !== 'delivered' && del.status !== 'failed' && (
                          <div className="flex gap-1">
                            {del.status === 'pending' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateDelivery(del._id || del.id, 'in_transit')}>In transito</Button>}
                            {del.status === 'in_transit' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateDelivery(del._id || del.id, 'delivered')}>Consegnata</Button>}
                            {del.status === 'scheduled' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateDelivery(del._id || del.id, 'in_transit')}>In transito</Button>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nessuna consegna registrata</p>
              )}
            </CardContent>
          </Card>

          {/* Invoice */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs uppercase font-semibold text-muted-foreground tracking-wider">Fattura</CardTitle>
                {!hasInvoice && order.status !== 'cancelled' && (
                  <Button size="sm" onClick={() => setShowInvoice(true)} data-testid="create-invoice-btn">
                    <FileCheck size={13} className="mr-1.5" />Genera Fattura
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {hasInvoice ? (
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                  <div className="flex items-center gap-3">
                    <FileText size={18} className="text-primary" />
                    <div>
                      <p className="font-mono font-semibold text-sm">{order.invoice.invoice_number}</p>
                      <p className="text-xs text-muted-foreground">{formatEuro(order.invoice.total)} • Scadenza: {order.invoice.due_date ? new Date(order.invoice.due_date).toLocaleDateString('it-IT') : '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      order.invoice.status === 'paid' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      order.invoice.status === 'overdue' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                      order.invoice.status === 'sent' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                      'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                    }`}>
                      {order.invoice.status === 'paid' ? 'Pagata' : order.invoice.status === 'overdue' ? 'Scaduta' : order.invoice.status === 'sent' ? 'Inviata' : 'Bozza'}
                    </span>
                    <Button variant="outline" size="sm" onClick={() => navigate(`/invoices?id=${order.invoice._id || order.invoice.id}`)}>
                      Apri
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nessuna fattura generata</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delivery Dialog */}
      <Dialog open={showDelivery} onOpenChange={setShowDelivery}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuova Consegna</DialogTitle></DialogHeader>
          <form onSubmit={createDelivery} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Data Consegna Programmata</Label>
              <Input type="date" value={delForm.scheduled_date} onChange={e => setDelForm(f => ({ ...f, scheduled_date: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Corriere / Vettore</Label>
              <Input value={delForm.carrier} onChange={e => setDelForm(f => ({ ...f, carrier: e.target.value }))} placeholder="BRT, SDA, GLS..." />
            </div>
            <div className="space-y-1.5">
              <Label>Numero Tracking</Label>
              <Input value={delForm.tracking_number} onChange={e => setDelForm(f => ({ ...f, tracking_number: e.target.value }))} placeholder="IT123456789" />
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={delForm.notes} onChange={e => setDelForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowDelivery(false)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="save-delivery-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crea
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={showInvoice} onOpenChange={setShowInvoice}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Genera Fattura</DialogTitle></DialogHeader>
          <form onSubmit={createInvoice} className="space-y-4 mt-2">
            <div className="p-3 bg-muted/30 rounded-lg text-sm">
              <p className="text-muted-foreground mb-1">Fattura per: <span className="font-medium text-foreground">{order.company_name}</span></p>
              <p className="text-muted-foreground">Totale: <span className="font-bold text-primary">{formatEuro(order.total)}</span></p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Scadenza (giorni)</Label>
                <Input type="number" value={dueDays} onChange={e => setDueDays(parseInt(e.target.value) || 30)} min="0" />
              </div>
              <div className="space-y-1.5">
                <Label>Metodo di pagamento</Label>
                <Select value={payMethod} onValueChange={setPayMethod}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bonifico">Bonifico bancario</SelectItem>
                    <SelectItem value="contanti">Contanti</SelectItem>
                    <SelectItem value="carta_credito">Carta di credito</SelectItem>
                    <SelectItem value="assegno">Assegno</SelectItem>
                    <SelectItem value="riba">RiBa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Note fattura</Label>
              <Textarea value={invNotes} onChange={e => setInvNotes(e.target.value)} rows={2} placeholder="Condizioni di pagamento, note per il cliente..." />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowInvoice(false)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="save-invoice-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Genera
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
